"use client"

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { api } from "../lib/axios"
import type { Kanban, Deal } from "../types/lead"

export const useDeals = () => {
  return useQuery({
    queryKey: ["deals"],
    queryFn: async (): Promise<Kanban[]> => {
      const response = await api.get("/deals/kanban")
      return response.data
    },
    staleTime: 2 * 60 * 1000,
    refetchInterval: 1000,
    gcTime: 0,
    refetchOnMount: 'always',
  })
}

export const useDeal = (dealId: string) => {
  return useQuery({
    queryKey: ["deal", dealId],
    queryFn: async (): Promise<Deal> => {
      const response = await api.get(`/deals/${dealId}`)
      return response.data
    },
    enabled: !!dealId,
    staleTime: 30,
    refetchInterval: 3000
  })
}

export const useUpdateDeal = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ dealId, data }: { dealId: string; data: Partial<Deal> }) => {
      const response = await api.put(`/deals/${dealId}`, data)
      return response.data
    },
    onSuccess: (_, { dealId }) => {
      queryClient.invalidateQueries({ queryKey: ["deal", dealId] })
      queryClient.invalidateQueries({ queryKey: ["deals"] })
    },
  })
}

export const useNegotiation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ dealId, negotiation }: { dealId: string; negotiation: boolean }) => {
      const response = await api.put(`/deals/negotiation/${dealId}`, { negotiation })
      return response.data
    },
    onSuccess: (_, { dealId }) => {
      queryClient.invalidateQueries({ queryKey: ["deal", dealId] })
      queryClient.invalidateQueries({ queryKey: ["deals"] })
    },
  })
}